import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Store } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/context/SeoContext";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Merci ! Nous vous répondrons entre deux récoltes.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Seo pageKey="contact" />
      <div className="mb-10">
        <p className="eyebrow">Contact</p>
        <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-bark sm:text-5xl">
          Écrivez-nous, ou passez à la miellerie
        </h1>
      </div>

      <div className="grid gap-10 lg:grid-cols-5">
        <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm lg:col-span-3">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-bark">Votre nom</label>
              <input
                id="contact-name"
                data-testid="contact-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
                placeholder="Marie Dupont"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-bark">Votre e-mail</label>
              <input
                id="contact-email"
                data-testid="contact-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
                placeholder="marie@exemple.fr"
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-bark">Votre message</label>
            <textarea
              id="contact-message"
              data-testid="contact-message"
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full resize-none rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
              placeholder="Une question sur nos miels, une commande spéciale, une visite du rucher…"
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="rounded-full bg-amber-deep px-8 py-3.5 font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {loading ? "Envoi…" : "Envoyer le message"}
          </button>
        </form>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-amber-800/15 bg-sage-light p-8">
            <MapPin size={24} className="text-sage" />
            <h2 className="mt-3 font-serif text-xl font-semibold text-bark">La miellerie</h2>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              23 Place de Saint-Sauveur<br />38220 Notre-Dame-de-Mésage, Isère<br />
              Visites du rucher sur rendez-vous, d'avril à septembre.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-800/15 bg-white p-8">
            <Store size={24} className="text-honey" />
            <h2 className="mt-3 font-serif text-xl font-semibold text-bark">Au marché</h2>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              Chaque samedi matin, place du marché de Vizille — venez goûter avant d'acheter.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-800/15 bg-white p-8">
            <Mail size={24} className="text-terra" />
            <h2 className="mt-3 font-serif text-xl font-semibold text-bark">Par écrit</h2>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              bonjour@petales-de-miel.fr<br />04 76 00 00 00
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
